import clsx from 'clsx';
import React from 'react';
import AddIcon from '../../assets/icons/add-icon.svg?react';
import Button from '../Button';
import { useButtonSizeSmall } from '../../utils/buttonSizes';
import Input from '../Input';
import { useFormik } from 'formik';
import { useApiClients } from '../../client/api/useApiClient';
import { toast } from 'react-toastify';
import { OwnerWithOrganizationDetails } from '@EHSNavigator/backend';
import { getExtendSeatSchema } from './schema';
import { queryClient } from '../../main';


interface ExtendSeatModalProps {
  otherClasses?: string;
  handleClose: () => void;
  selectedOwner: OwnerWithOrganizationDetails | null;
  onSuccess?: () => void;
  queryParams?: {
    search: string;
    page: number;
    limit: number;
  };
}

const ExtendSeatModal: React.FC<ExtendSeatModalProps> = ({
  otherClasses,
  handleClose,
  selectedOwner,
  onSuccess,
  queryParams,
}) => {
  const extendSeatModalClasses = clsx(otherClasses);
  const { OnboardOrganizationApi } = useApiClients();

  const formHandler = useFormik({
    initialValues: {
      seatCount: '',
      amount: '',
    },
    validationSchema: getExtendSeatSchema(),
    onSubmit: async (values) => {
      try {
        const response = await OnboardOrganizationApi.onboardOrganizationControllerExtendSeat({
          extendSeatDto: {
            organizationId: selectedOwner?.org?.id as string,
            licenseCount: Number(values.seatCount),
            amount: values.amount ? Number(values.amount) : undefined,
          },
        });

        // Update cache after successful API call
        if (queryParams && selectedOwner?.org?.id) {
          queryClient.setQueryData(
            [
              'get-admin-organizations',
              queryParams.search,
              queryParams.page,
              queryParams.limit,
            ],
            (oldData: any) => {
              if (!oldData) return oldData;

              return {
                ...oldData,
                owners: oldData.owners?.map((owner: OwnerWithOrganizationDetails) => {
                  if (owner.org?.id === selectedOwner?.org?.id) {
                    return {
                      ...owner,
                      org: {
                        ...owner.org,
                        manualSubscription: {
                          ...owner.org?.manualSubscription,
                          licenseCount: Number(values.seatCount) + Number(owner.org?.manualSubscription?.licenseCount),
                          ...(values.amount ? { amount: Number(values.amount) + Number(owner.org?.manualSubscription?.amount) } : {}),
                        },
                      },
                    };
                  }
                  return owner;
                }),
              };
            },
          );
        }

        toast.success(response?.data?.message || 'Seats extended successfully');
        handleClose();
        formHandler.resetForm();
        if (onSuccess) {
          onSuccess();
        }
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });

  return (
    <div
      className={extendSeatModalClasses}
      data-testid="extend-seat-modal"
    >
      <div className="mb-8 flex w-full flex-col items-center gap-2.5 text-center lg:mb-12">
        <AddIcon className="[&>path]:fill-brand-byzantine h-12 w-12 md:h-85 md:w-85" />
        <h4 className="text-brand-byzantine">Extend Seats</h4>
        <p className="font-inter-variable -tracking-form-input text-neutral-tones-text/70 mt-2 text-sm leading-5 font-normal max-w-md">
          The entered value will be added to the current count. (e.g., Previous: 10, You type: 5, Result: 15)
        </p>
      </div>
      <form action="#" onSubmit={formHandler?.handleSubmit}>
        <div className="mt-2.5 grid grid-cols-1 gap-6">
          <Input
            onBlur={formHandler?.handleBlur}
            label="License Count*"
            errorMessage={
              formHandler?.touched.seatCount && formHandler?.errors.seatCount
                ? formHandler?.errors.seatCount
                : ''
            }
            disabled={formHandler?.isSubmitting}
            value={formHandler?.values.seatCount}
            type="number"
            placeholder="Enter number of seats"
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d+$/.test(value)) {
                formHandler?.setFieldValue('seatCount', value, true);
              }
            }}
            name="seatCount"
            otherClasses={{ container: 'w-full' }}
            autoComplete="new-password"
          />
          <Input
            onBlur={formHandler?.handleBlur}
            label={
              <>
                Amount <em className="text-sm font-normal">(Optional)</em>
              </>
            }
            errorMessage={
              formHandler?.touched.amount && formHandler?.errors.amount
                ? formHandler?.errors.amount
                : ''
            }
            disabled={formHandler?.isSubmitting}
            value={formHandler?.values.amount}
            type="text"
            placeholder="Enter amount"
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
                formHandler?.setFieldValue('amount', value, true);
              }
            }}
            name="amount"
            otherClasses={{ container: 'w-full' }}
            autoComplete="new-password"
          />
        </div>
        <div className="mt-8 flex flex-col-reverse gap-15 md:grid md:grid-cols-2 md:gap-6 lg:mt-12">
          <Button
            variant="secondary"
            type="button"
            size={useButtonSizeSmall()}
            onClick={() => {
              formHandler.resetForm();
              handleClose();
            }}
            otherClasses="border-transparent text-brand-posh-pink hover:border-brand-posh-pink hover:text-brand-posh-pink"
          >
            Cancel
          </Button>
          <Button
            loading={formHandler?.isSubmitting}
            disable={formHandler?.isSubmitting}
            type="submit"
            size={useButtonSizeSmall()}
          >
            Extend Seats
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ExtendSeatModal;